import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { SovosButton } from "mosaic-react";
import { AutoAwesome, CheckCircle } from "mosaic-react-icons";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks, hasModule } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    getImportProfileByIdAction,
    getAvailableConfigSetsAction,
    getAvailableEntitiesAction,
    getAvailableCdmFieldsAction,
    createImportProfileMappingAction,
    getImportProfileMappingByProfileAction,
    suggestCustomTemplateEntityAction,
    suggestCustomTemplateMappingAction,
    updateImportProfileMappingAction,
    runCustomTemplateMappingAction,
    getImportProfileIssuesAction,
    fixImportProfileCellAction,
    applyTranslatorToStagingAction,
    createProfileTranslationRuleAction,
    triggerIssueDetectionAction,
    getLinkedTranslationTablesAction,
    suggestDataFixesAction,
    acceptDataFixSuggestionAction,
    acceptDataFixesAction,
} from "../../store/actions";
import ColumnMappingCanvas from "../../store/views/MappingFlow/ColumnMappingCanvas";
import { filteredQueriesByModules } from "../../routes/manageRouting";
import Header from "./ColumnMappingFlowHeader";
import { mapProperties } from "colbi_web_ui/lib/utils";

const ColumnMappingFlow = (props) => {
    const dispatch = useDispatch();
    const [canvasStep, setCanvasStep] = useState(null);
    const [canvasImported, setCanvasImported] = useState(false);
    const [canvasActions, setCanvasActions] = useState({});
    const [importProfileName, setImportProfileName] = useState("");

    const handleStepChange = useCallback(({ step, imported }) => {
        setCanvasStep(step);
        setCanvasImported(imported);
    }, []);

    const handleActionStateChange = useCallback((actions) => {
        setCanvasActions(actions);
    }, []);
    const dispatchGetAvailableConfigSets = useCallback(
        (projectModules) => dispatch(getAvailableConfigSetsAction(projectModules)),
        [dispatch],
    );
    const dispatchGetImportProfileById = useCallback(
        (currentProjectId, currentProfileId) => dispatch(getImportProfileByIdAction(currentProjectId, currentProfileId)),
        [dispatch],
    );
    const dispatchGetAvailableEntities = useCallback(
        (configSet) => dispatch(getAvailableEntitiesAction(configSet)),
        [dispatch],
    );
    const dispatchGetAvailableCdmFields = useCallback(
        (configSet, entityName, fileType) => dispatch(getAvailableCdmFieldsAction(configSet, entityName, fileType)),
        [dispatch],
    );
    const dispatchCreateImportProfileMapping = useCallback(
        (payload) => dispatch(createImportProfileMappingAction(payload)),
        [dispatch],
    );
    const dispatchGetImportProfileMappingByProfile = useCallback(
        (profileId) => dispatch(getImportProfileMappingByProfileAction(profileId)),
        [dispatch],
    );
    const dispatchUpdateImportProfileMapping = useCallback(
        (mappingId, data) => dispatch(updateImportProfileMappingAction(mappingId, data)),
        [dispatch],
    );
    const dispatchRunCustomTemplateMapping = useCallback(
        (payload) => dispatch(runCustomTemplateMappingAction(payload)),
        [dispatch],
    );
    const {
        modules,
        route,
        location,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
        pushModal,
        reload,
    } = useControllerInitialization(props);

    const [availableConfigSets, setAvailableConfigSets] = useState(null);

    useEffect(() => {
        if (!modules?.length) return;
        const moduleNames = modules
            .map((m) => (typeof m === "string" ? m : m?.name))
            .filter(Boolean);
        if (!moduleNames.length) return;
        dispatchGetAvailableConfigSets(moduleNames).then((result) => {
            setAvailableConfigSets(result || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modules]);

    let { fiscalYear, importId, entityRawData } = matchParams;
    const profileId = parseInt(entityRawData, 10) || undefined;
    const hasAiSuggestions = hasModule(modules, "ai_suggestions");

    useEffect(() => {
        let isActive = true;

        if (!projectId || !profileId) {
            setImportProfileName("");
            return () => {
                isActive = false;
            };
        }

        setImportProfileName("");
        dispatchGetImportProfileById(projectId, profileId).then((profile) => {
            if (!isActive) return;
            if (String(profile?.id ?? "") !== String(profileId)) return;
            setImportProfileName(profile?.profile_name || "");
        });

        return () => {
            isActive = false;
        };
    }, [dispatchGetImportProfileById, profileId, projectId]);
    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const dispatchSuggestCustomTemplateEntity = useCallback(
        (profileId, configSet, availableEntities) =>
            dispatch(suggestCustomTemplateEntityAction(profileId, configSet, availableEntities, organizationProjects?.id?.toString() || "")),
        [dispatch, organizationProjects],
    );
    const dispatchSuggestCustomTemplateMapping = useCallback(
        (profileId, configSet, entityName, conversationId) =>
            dispatch(suggestCustomTemplateMappingAction(profileId, configSet, entityName, organizationProjects?.id?.toString() || "", conversationId)),
        [dispatch, organizationProjects],
    );
    const dispatchGetImportProfileIssues = useCallback(
        (profileId, mappingId, configSet, entityName, projectId) =>
            dispatch(getImportProfileIssuesAction(profileId, mappingId, configSet, entityName, projectId)),
        [dispatch],
    );
    const dispatchFixImportProfileCell = useCallback(
        (profileId, projectId, clbId, column, value) =>
            dispatch(fixImportProfileCellAction(profileId, projectId, clbId, column, value)),
        [dispatch],
    );
    const dispatchSuggestDataFixes = useCallback(
        (profileId, projectId, columnNames) =>
            dispatch(suggestDataFixesAction(profileId, projectId, organizationProjects?.id?.toString() || "", columnNames)),
        [dispatch, organizationProjects],
    );
    const dispatchAcceptDataFix = useCallback(
        (profileId, projectId, clbId, column, newValue) =>
            dispatch(acceptDataFixSuggestionAction(profileId, projectId, clbId, column, newValue)),
        [dispatch],
    );
    const dispatchBulkAcceptDataFixes = useCallback(
        (profileId, projectId, fixes) =>
            dispatch(acceptDataFixesAction(profileId, projectId, fixes)),
        [dispatch],
    );
    const dispatchApplyTranslatorToStaging = useCallback(
        (profileId, projectId, translationTableId) =>
            dispatch(applyTranslatorToStagingAction(profileId, projectId, translationTableId)),
        [dispatch],
    );
    const dispatchGetLinkedTranslationTables = useCallback(
        (profileId, projectId) =>
            dispatch(getLinkedTranslationTablesAction(profileId, projectId)),
        [dispatch],
    );
    const dispatchCreateProfileTranslationRule = useCallback(
        (profileId, column, findPattern, replaceValue, name) =>
            dispatch(createProfileTranslationRuleAction(profileId, column, findPattern, replaceValue, name)),
        [dispatch],
    );
    const dispatchTriggerIssueDetection = useCallback(
        (profileId, mappingId, projectId) =>
            dispatch(triggerIssueDetectionAction(profileId, mappingId, projectId)),
        [dispatch],
    );

    const { loading = true, success = false } = queryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "ColumnMappingFlow");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const { items, headerItems, metadata, filters, rawDataEdit } = mapProperties(
        {
            items: "query(0).items",
            headerItems: "query(0).headerItems",
            metadata: "query(0).metadata",
            filters: "query(0).filters",
            rawDataEdit: "query(0).rawDataEdit",
        },
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );


    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
        repositoryStatus: { status: 1 },
    };

    if (loading) {
        return LoadingComponent;
    }

    const { step: s, imported: imp, allMapped, savingMapping, loadingAiMapping, selectedEntityName,
        onCancel, onChangeEntity, onNext, onBack, onProceed, onSuggestMapping: onSuggestMappingAction,
        onOpenAiFixPanel,
    } = canvasActions;

    const headerActions = [
        imp ? null : s === 2 && onChangeEntity ? (
            <SovosButton key="change-entity" variant="text" onClick={onChangeEntity}>Change entity</SovosButton>
        ) : null,
        imp ? null : s === 2 && onCancel ? (
            <SovosButton key="cancel" variant="text" onClick={onCancel}>Cancel</SovosButton>
        ) : null,
        imp ? null : s === 2 && onSuggestMappingAction && selectedEntityName ? (
            <SovosButton
                key="ai"
                variant="outlined"
                size="small"
                startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                onClick={onSuggestMappingAction}
                disabled={loadingAiMapping}
            >
                {loadingAiMapping ? "Suggesting..." : "Suggest mappings (AI)"}
            </SovosButton>
        ) : null,
        imp ? null : s === 2 ? (
            <SovosButton key="next" variant="contained" disabled={!allMapped} onClick={onNext}>Next</SovosButton>
        ) : null,
        imp ? null : s === 3 && onBack ? (
            <SovosButton key="back" variant="text" onClick={onBack}>Back</SovosButton>
        ) : null,
        imp ? null : s === 3 && onOpenAiFixPanel ? (
            <SovosButton
                key="ai-fix"
                variant="outlined"
                size="small"
                startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                onClick={onOpenAiFixPanel}
            >
                Suggest AI Corrections
            </SovosButton>
        ) : null,
        imp ? null : s === 3 && onProceed ? (
            <SovosButton key="proceed" variant="contained" startIcon={<CheckCircle fontSize="small" />} onClick={onProceed} disabled={savingMapping}>
                {savingMapping ? "Saving..." : "Proceed"}
            </SovosButton>
        ) : null,
    ].filter(Boolean);

    return (
        <GlobalPageLayout
            globalLayoutProps={globalLayoutProps}
            modules={modules}
            headerProps={{
                fiscalYears: undefined,
                importIds: undefined,
                downloadableQueries,
                title: "",
                headerComponents,
                customHeader: (
                    <Header
                        step={canvasStep}
                        frameworkName={importProfileName}
                        imported={canvasImported}
                        actions={headerActions}
                        onStepClick={(stepNum) => canvasActions.onStepClick?.(stepNum)}
                    />
                ),
            }}
            headerComponentsMapper={() => (headerComponentProperties) => (
                <headerComponentProperties.Component
                    key="column-mapping"
                    pushModal={pushModal}
                    i18n={i18n}
                    reload={reload}
                    projectId={projectId}
                    user={userInformation}
                    {...headerComponentProperties.props}
                />
            )}
            fetch={props.fetch}
            route={route}
            matchParams={matchParams}
            menu={props.menu}
            isLoadingContent={() => false}
        >
            <ColumnMappingCanvas
                items={items}
                headerItems={headerItems}
                metadata={metadata}
                filters={filters}
                rawDataEdit={rawDataEdit}
                profileId={profileId}
                projectId={projectId}
                fiscalYear={fiscalYear}
                importId={parseInt(importId, 10) || undefined}
                projectModules={project?.modules?.length ? project.modules : modules || []}
                availableConfigSets={availableConfigSets}
                onFetchConfigSets={dispatchGetAvailableConfigSets}
                onFetchEntities={dispatchGetAvailableEntities}
                onFetchCdmFields={dispatchGetAvailableCdmFields}
                onFetchExistingMapping={dispatchGetImportProfileMappingByProfile}
                onCreateMapping={dispatchCreateImportProfileMapping}
                onUpdateMapping={dispatchUpdateImportProfileMapping}
                onRunMapping={dispatchRunCustomTemplateMapping}
                onSuggestEntity={hasAiSuggestions ? dispatchSuggestCustomTemplateEntity : undefined}
                onSuggestMapping={hasAiSuggestions ? dispatchSuggestCustomTemplateMapping : undefined}
                onFetchIssues={dispatchGetImportProfileIssues}
                onFixCell={dispatchFixImportProfileCell}
                onApplyTranslator={dispatchApplyTranslatorToStaging}
                onCreateTranslationRule={hasAiSuggestions ? dispatchCreateProfileTranslationRule : undefined}
                onFetchLinkedTranslationTables={dispatchGetLinkedTranslationTables}
                onRunIssueDetection={dispatchTriggerIssueDetection}
                onSuggestFixes={hasAiSuggestions ? dispatchSuggestDataFixes : undefined}
                onAcceptFix={hasAiSuggestions ? dispatchAcceptDataFix : undefined}
                onBulkAcceptFixes={hasAiSuggestions ? dispatchBulkAcceptDataFixes : undefined}
                onStepChange={handleStepChange}
                onActionStateChange={handleActionStateChange}
            />
        </GlobalPageLayout>
    );
};

ColumnMappingFlow.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "custom_csv_raw_data",
            args: {
                entity: args.entityRawData || "",
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
            },
            body: "items,headerItems,metadata{total},rawDataEdit",
            isDatagridReq: true,
        },
    ];
    return [...allQueries, ...deepCopyQueries];
};

export default connect(
    ({ user, fetch, menu }) => ({ user, fetch, menu }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
    },
)(withRouter(ColumnMappingFlow));
